import React from "react";
import { denormalize } from "normalizr";
import { useIntl } from "react-intl";

import Box from "@mui/material/Box";
import { TCatalog, TVehicle } from "types";
import { vehicleListSchema } from "schemas";

import { CatalogStore } from "stores";

import { findSimilarVehicles } from "./helper";

import Card from "pages/Catalog/containers/Modell/Card";
import Modifier from "./Modifier";

import { useSmartNavigate } from "helper/SmartLink";

interface ISuggestion {
  example: TVehicle;
  catalog: TCatalog;
  length?: number;
}

export const Suggestion: React.FC<ISuggestion> = ({
  example,
  catalog,
  length = 4,
}): JSX.Element | null => {
  const navigate = useSmartNavigate();
  const { formatMessage: t } = useIntl();

  const [criteria, setCriteria] = React.useState<[string, ...string[]]>([
    "modell",
  ]);
  const [suggestions, setSuggestions] = React.useState<TVehicle[]>([]);

  const vehicles: TVehicle[] = denormalize(
    catalog.result.vehicle,
    vehicleListSchema,
    catalog.entities,
  );

  React.useEffect(() => {
    const suggest = () => {
      const suggestions: TVehicle[] = findSimilarVehicles({
        example,
        vehicles,
        criteria,
      }).slice(0, length);

      setSuggestions(suggestions);
    };
    suggest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [criteria, example]);

  const options: any = {
    marke: example.modell.marke.name,
    modell: example.modell.name,
    aufbau: t({ id: "aufbau." + example.aufbau?.id + ".label" }),
    fzart: t({ id: "fzart." + example.fzart?.id + ".label" }),
    farbe_aussen: example.farbe_aussen?.name,
  };

  if (example.kraftstoff) {
    options.kraftstoff = t({
      id: "kraftstoff." + example.kraftstoff?.id + ".label",
    });
  }

  return (
    <>
      <Box sx={{ py: 2 }}>
        <Modifier
          onChangeHandler={setCriteria}
          value={criteria}
          options={options}
        />
      </Box>
      <Box
        data-testid="suggestions"
        sx={{
          display: "grid",
          columnGap: { xs: 1, sm: 2, md: 3, lg: 4 },
          rowGap: { xs: 1, sm: 2, md: 3, lg: 4 },
          gridTemplateColumns: {
            lg: "repeat(4, 1fr)",
            md: "repeat(3, 1fr)",
            sm: "repeat(2, 1fr)",
            xs: "repeat(1, 1fr)",
          },
        }}
      >
        {suggestions.map((vehicle: TVehicle) => {
          return (
            <Card
              vehicle={vehicle}
              key={vehicle.wgnr}
              onClickHandler={() => navigate("/catalog/dt/" + vehicle.wgnr)}
            />
          );
        })}
      </Box>
    </>
  );
};

interface IPipeline {
  example: TVehicle;
  length?: number;
}

const Pipeline: React.FC<IPipeline> = ({
  example,
  length = 4,
}): JSX.Element | null => {
  const { state: catalog } = React.useContext(CatalogStore);
  if (!catalog.entities.vehicle) return null;
  return <Suggestion {...{ catalog, example, length }} />;
};

export default Pipeline;
